import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import {
  CardElement,
  Elements,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';

import Icon from '../components/Icon';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import { useToast } from '../components/Toast';
import {
  BillingCycle,
  SubscriptionPlan,
  formatPlanPrice,
  listSubscriptionPlans,
} from '../api/subscriptionPlans';
import {
  UserSubscription,
  cancelSubscription,
  getMySubscription,
  resumeSubscription,
  subscribeToPlan,
} from '../api/subscriptions';
import { extractApiError } from '../api/legalServices';
import { getStripePublicConfig } from '../api/stripeSettings';

// ─── Stripe instance ───────────────────────────────────────────────
// The publishable key comes from the backend (admin → Settings → Stripe Payments), so a
// test/live switch takes effect without rebuilding the frontend. One
// loadStripe() per key; if no key is available the page still renders and
// the pay buttons explain that payments are unavailable.
const stripeByKey = new Map<string, Promise<Stripe | null>>();
const stripeFor = (publishableKey: string): Promise<Stripe | null> => {
  let instance = stripeByKey.get(publishableKey);
  if (!instance) {
    instance = loadStripe(publishableKey);
    stripeByKey.set(publishableKey, instance);
  }
  return instance;
};

const STRIPE_UNAVAILABLE = 'Payments are unavailable right now — Stripe is not configured.';

// ─── Card form (rendered inside the Elements provider) ─────────────
interface PaymentFormProps {
  plan: SubscriptionPlan;
  /** Trial that applies to this agent — 0 for returning subscribers. */
  trialDays: number;
  onSuccess: (sub: UserSubscription) => void;
  onCancel: () => void;
}

const cardOptions = {
  style: {
    base: {
      fontSize: '15px',
      color: '#1A1A1A',
      fontFamily: 'Satoshi, DM Sans, system-ui, sans-serif',
      '::placeholder': { color: '#9CA3AF' },
    },
    invalid: { color: '#DC2626' },
  },
};

const PaymentForm: React.FC<PaymentFormProps> = ({ plan, trialDays, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { amount, suffix } = formatPlanPrice(plan.price, plan.billingCycle);

  const handlePay = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!stripe || !elements) {
      setError('Stripe is still loading. Try again in a moment.');
      return;
    }
    const card = elements.getElement(CardElement);
    if (!card) {
      setError('Card field is not mounted.');
      return;
    }

    setSubmitting(true);
    try {
      // 1) Tokenise the card in the browser — the number never touches our server.
      const { token, error: stripeErr } = await stripe.createToken(card);
      if (stripeErr) {
        setError(stripeErr.message || 'Could not validate the card.');
        return;
      }
      if (!token) {
        setError('Card token was not returned by Stripe.');
        return;
      }

      // 2) POST to our backend with { planId, stripeToken } — backend creates
      //    the Stripe Customer + Subscription using this token.
      const sub = await subscribeToPlan({
        planId: plan.id,
        stripeToken: token.id,
      });
      onSuccess(sub);
    } catch (err) {
      setError(extractApiError(err, 'Subscription failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handlePay}>
      <div
        className="mb-5 p-4 rounded-xl"
        style={{
          background: 'var(--brand-bg)',
          border: '1px solid rgba(225,1,116,0.12)',
        }}
      >
        <div className="text-[12px] font-semibold uppercase text-g500 mb-1">
          You're subscribing to
        </div>
        <div className="flex items-baseline justify-between flex-wrap gap-x-3">
          <span className="text-[18px] font-extrabold text-ink">{plan.name}</span>
          <span>
            <span className="text-[20px] font-extrabold text-brand">{amount}</span>
            {suffix && <span className="text-g500 ml-1">{suffix}</span>}
          </span>
        </div>
        {trialDays > 0 && (
          <div
            className="mt-2 text-[12.5px] font-semibold"
            style={{ color: 'var(--brand)' }}
          >
            {trialDays}-day free trial — your card is saved now and first
            charged when the trial ends.
          </div>
        )}
      </div>

      <label className="form-label" htmlFor="card-element">
        Card details
      </label>
      <div
        id="card-element"
        className="form-input"
        style={{ padding: '14px 16px' }}
      >
        <CardElement options={cardOptions} />
      </div>
      {/* <div className="text-[12px] text-g400 mt-1.5">
        Test card: <code>4242 4242 4242 4242</code> · any future date · any CVC.
      </div> */}

      {error && (
        <div
          role="alert"
          className="mt-4 rounded-[10px] px-3 py-2.5 text-sm font-medium flex items-start gap-2"
          style={{
            background: 'var(--red-bg)',
            color: 'var(--red)',
            border: '1px solid rgba(220,38,38,.12)',
          }}
        >
          <Icon name="x-circle" size={16} color="var(--red)" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex flex-col-reverse sm:flex-row gap-2.5 justify-end mt-6">
        <button
          type="button"
          className="btn btn-secondary justify-center"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary justify-center"
          disabled={submitting || !stripe}
        >
          {submitting
            ? 'Processing...'
            : trialDays > 0
            ? `Start ${trialDays}-day free trial`
            : `Pay ${amount}${suffix}`}
        </button>
      </div>
    </form>
  );
};

// ─── Page ──────────────────────────────────────────────────────────
const AgentSubscription: React.FC = () => {
  const { toast } = useToast();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [cycle, setCycle] = useState<BillingCycle>('monthly');
  const [mySub, setMySub] = useState<UserSubscription | null>(null);
  // Free trial only for first-time subscribers (decided by the backend).
  const [trialEligible, setTrialEligible] = useState<boolean>(false);
  const [payingFor, setPayingFor] = useState<SubscriptionPlan | null>(null);
  const [confirmingCancel, setConfirmingCancel] = useState<boolean>(false);
  const [cancelling, setCancelling] = useState<boolean>(false);
  const [resuming, setResuming] = useState<boolean>(false);
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);

  const loadStripeConfig = useCallback(async () => {
    const { publishableKey } = await getStripePublicConfig();
    setStripePromise(stripeFor(publishableKey));
  }, []);

  useEffect(() => {
    loadStripeConfig().catch(() => {
      /* stays null — handleSwitchClick fetches it again */
    });
  }, [loadStripeConfig]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [{ plans: list }, me] = await Promise.all([
        listSubscriptionPlans({ active: true }),
        getMySubscription().catch(() => null),
      ]);
      setPlans(list);
      setMySub(me ? me.subscription : null);
      setTrialEligible(me ? me.trialEligible : false);
    } catch (err) {
      setLoadError(extractApiError(err, 'Failed to load plans'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const visiblePlans = useMemo(
    () => plans.filter((p) => p.billingCycle === cycle),
    [plans, cycle]
  );
  // With a single-plan catalog only one cycle may exist — hide the toggle
  // and snap to whichever cycle actually has plans.
  const availableCycles = useMemo(
    () =>
      (['monthly', 'yearly'] as BillingCycle[]).filter((c) =>
        plans.some((p) => p.billingCycle === c)
      ),
    [plans]
  );
  useEffect(() => {
    if (availableCycles.length > 0 && !availableCycles.includes(cycle)) {
      setCycle(availableCycles[0]);
    }
  }, [availableCycles, cycle]);
  // The latest row is only "current" while it still runs (a cancelled plan
  // runs until expiryDate) or while Stripe is still trying to bill it. Once
  // it has ended the agent is back on Free and can subscribe again.
  const notExpired =
    mySub !== null && new Date(mySub.expiryDate).getTime() > Date.now();
  const billingIssue =
    mySub !== null && (mySub.status === 'past_due' || mySub.status === 'incomplete');
  const currentSub =
    mySub !== null &&
    (billingIssue ||
      (notExpired &&
        (mySub.status === 'active' ||
          mySub.status === 'trialing' ||
          mySub.status === 'cancelled')))
      ? mySub
      : null;
  const currentPlanName = currentSub?.planName ?? 'Free';
  const isCancelledRunning = currentSub !== null && currentSub.status === 'cancelled';
  const fmtDate = (iso?: string | null) =>
    iso ? new Date(iso).toLocaleDateString() : '—';
  const trialDaysFor = (p: SubscriptionPlan) => (trialEligible ? p.trialDays ?? 0 : 0);

  const handlePaymentSuccess = (sub: UserSubscription) => {
    setMySub(sub);
    setTrialEligible(false);
    setPayingFor(null);
    toast(
      sub.status === 'trialing'
        ? `Free trial of ${sub.planName} started`
        : `Subscribed to ${sub.planName}`,
      'success'
    );
  };

  /**
   * Cancel the current subscription via PATCH /api/subscriptions/:id/cancel.
   * The backend marks the row 'cancelled' and (if Stripe-linked) tells Stripe
   * to cancel at period end, so the agent keeps access until expiryDate.
   */
  const handleConfirmCancel = async () => {
    if (!mySub || cancelling) return;
    setCancelling(true);
    try {
      const wasTrial = mySub.status === 'trialing';
      const updated = await cancelSubscription(mySub.id);
      setMySub(updated);
      toast(
        wasTrial
          ? `Free trial cancelled — you won't be charged. Access ends ${fmtDate(updated.expiryDate)}.`
          : `Your ${updated.planName} subscription has been cancelled.`,
        'success'
      );
      setConfirmingCancel(false);
    } catch (err) {
      toast(extractApiError(err, 'Failed to cancel subscription'), 'error');
    } finally {
      setCancelling(false);
    }
  };

  /** Undo a cancellation while the plan still runs (PATCH …/resume). */
  const handleResume = async () => {
    if (!currentSub || resuming) return;
    setResuming(true);
    try {
      const updated = await resumeSubscription(currentSub.id);
      setMySub(updated);
      toast(
        updated.status === 'trialing'
          ? `Your ${updated.planName} free trial is back on`
          : `Your ${updated.planName} subscription will renew again`,
        'success'
      );
    } catch (err) {
      toast(extractApiError(err, 'Failed to resume subscription'), 'error');
      load();
    } finally {
      setResuming(false);
    }
  };

  // The cancel CTA only makes sense for a real paid, still-active subscription.
  const canCancel =
    currentSub !== null &&
    (currentSub.status === 'active' || currentSub.status === 'trialing') &&
    Number(currentSub.amount) > 0;

  // On a paid plan (even a cancelled one whose access hasn't expired yet),
  // "Switch to Free" is pointless — Free happens automatically at expiry.
  const onPaidPlan = currentSub !== null && Number(currentSub.amount) > 0;

  // The chosen plan must satisfy two preconditions before we even open the
  // modal: Stripe must be configured on the frontend, and the plan must have
  // a Stripe price for the chosen cycle (set by an admin via /api/plans).
  const handleSwitchClick = async (p: SubscriptionPlan) => {
    if (!stripePromise) {
      // The key fetch at mount failed or hasn't finished — try it again, and
      // only blame the configuration when the server says so (503).
      try {
        await loadStripeConfig();
      } catch (err) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        toast(
          status === 503 ? STRIPE_UNAVAILABLE : 'Could not load payment settings. Please try again.',
          'error'
        );
        return;
      }
    }
    const priceId =
      p.billingCycle === 'yearly' ? p.stripeYearlyPriceId : p.stripeMonthlyPriceId;
    if (!priceId) {
      toast(
        'This plan is not yet published in Stripe. Ask an admin to save it once.',
        'error'
      );
      return;
    }
    setPayingFor(p);
  };

  return (
    <div>
      {/* Current plan banner */}
      <div
        className="mb-5 sm:mb-6 flex justify-between items-center flex-wrap gap-4 p-5 sm:px-7 sm:py-6"
        style={{
          borderRadius: 20,
          background:
            'linear-gradient(135deg, rgba(225,1,116,0.06) 0%, rgba(225,1,116,0.02) 60%, rgba(37,99,235,0.04) 100%)',
          border: '1px solid rgba(225,1,116,0.10)',
        }}
      >
        <div className="flex items-center gap-3 sm:gap-4 min-w-0">
          <div
            className="rounded-2xl flex items-center justify-center flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14"
            style={{ background: 'var(--brand)' }}
          >
            <Icon name="star" size={26} color="#fff" />
          </div>
          <div className="min-w-0">
            <div
              className="font-semibold uppercase text-g400 mb-1"
              style={{ fontSize: 11, letterSpacing: '0.8px' }}
            >
              Current Plan
            </div>
            <h2
              className="font-extrabold text-ink text-xl sm:text-[22px]"
              style={{ letterSpacing: '-0.4px' }}
            >
              {currentPlanName} Plan
            </h2>
            <div className="text-g500" style={{ fontSize: 13, marginTop: 2 }}>
              {!currentSub
                ? mySub
                  ? `Your ${mySub.planName} plan ended ${fmtDate(mySub.expiryDate)}`
                  : 'No active subscription'
                : currentSub.status === 'cancelled'
                ? `Cancelled · access remains until ${fmtDate(currentSub.expiryDate)}`
                : currentSub.status === 'trialing'
                ? `Free trial · first charge of ${
                    formatPlanPrice(currentSub.amount, currentSub.billingCycle).amount
                  } on ${fmtDate(currentSub.trialEndsAt ?? currentSub.expiryDate)}`
                : billingIssue
                ? 'Payment failed · update your card — Stripe will retry'
                : `Renews ${fmtDate(currentSub.expiryDate)} · ${
                    currentSub.autoRenew ? 'auto-renew on' : 'auto-renew off'
                  }`}
            </div>
          </div>
        </div>
        {canCancel && (
          <div className="flex items-center gap-2.5 w-full sm:w-auto">
            <button
              className="btn btn-secondary w-full justify-center sm:w-auto"
              style={{ color: 'var(--red)' }}
              onClick={() => setConfirmingCancel(true)}
              disabled={cancelling}
            >
              <Icon name="x-circle" size={14} color="var(--red)" />{' '}
              {currentSub?.status === 'trialing' ? 'Cancel free trial' : 'Cancel subscription'}
            </button>
          </div>
        )}
        {isCancelledRunning && (
          <div className="flex items-center gap-2.5 w-full sm:w-auto">
            <button
              className="btn btn-primary w-full justify-center sm:w-auto"
              onClick={handleResume}
              disabled={resuming}
            >
              <Icon name="check-circle" size={14} color="#fff" />{' '}
              {resuming ? 'Resuming…' : 'Resume subscription'}
            </button>
          </div>
        )}
      </div>

      {/* Cycle toggle */}
      <div className="flex justify-between items-center flex-wrap gap-3 mb-5">
        <div>
          <h3
            className="font-bold text-ink"
            style={{ fontSize: 17, letterSpacing: '-0.3px' }}
          >
            {plans.length === 1 ? 'Your plan' : 'Compare plans'}
          </h3>
          <p className="text-g500" style={{ fontSize: 13, marginTop: 2 }}>
            {plans.length === 1
              ? 'One simple plan — everything included.'
              : `Switch billing cycle to see ${cycle === 'monthly' ? 'monthly' : 'yearly'} pricing.`}
          </p>
        </div>
        {availableCycles.length > 1 && (
        <div
          className="flex p-0.5 gap-0.5 rounded-[10px]"
          style={{ background: 'var(--g100)' }}
        >
          {(['monthly', 'yearly'] as BillingCycle[]).map((c) => (
            <button
              key={c}
              onClick={() => setCycle(c)}
              className="capitalize"
              style={{
                padding: '7px 18px',
                fontSize: 13,
                fontWeight: 600,
                borderRadius: 8,
                background: cycle === c ? '#fff' : 'transparent',
                color: cycle === c ? 'var(--ink)' : 'var(--g500)',
                border: 'none',
                cursor: 'pointer',
                boxShadow: cycle === c ? '0 1px 3px rgba(0,0,0,.08)' : undefined,
              }}
            >
              {c}
              {c === 'yearly' && (
                <span
                  style={{
                    marginLeft: 6,
                    fontSize: 10,
                    padding: '1px 6px',
                    borderRadius: 4,
                    background: 'var(--green-bg)',
                    color: 'var(--green)',
                    fontWeight: 700,
                  }}
                >
                  SAVE
                </span>
              )}
            </button>
          ))}
        </div>
        )}
      </div>

      {loading && (
        <div className="card text-center py-12 text-g500">
          <Icon name="package" size={24} color="var(--g400)" /> Loading plans...
        </div>
      )}

      {loadError && (
        <div
          className="card p-5"
          style={{
            background: 'var(--red-bg)',
            color: 'var(--red)',
            border: '1px solid rgba(220,38,38,.12)',
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Icon name="x-circle" size={18} color="var(--red)" />
            <strong>Couldn't load plans</strong>
          </div>
          <div className="text-sm mb-3">{loadError}</div>
          <button className="btn btn-secondary btn-sm" onClick={load}>
            Try again
          </button>
        </div>
      )}

      {!loading && !loadError && visiblePlans.length === 0 && (
        <div className="card text-center px-5 py-12 sm:p-16">
          <div
            className="rounded-[20px] mx-auto flex items-center justify-center mb-4"
            style={{ width: 64, height: 64, background: 'var(--g100)' }}
          >
            <Icon name="credit-card" size={28} color="var(--g400)" />
          </div>
          <h3 className="font-bold text-ink mb-1" style={{ fontSize: 17 }}>
            No {cycle} plans available
          </h3>
          <p className="text-g500" style={{ fontSize: 13 }}>
            Your admin hasn't published any {cycle} subscription plans yet.
          </p>
        </div>
      )}

      {!loading && !loadError && visiblePlans.length > 0 && (
        <div
          className="grid gap-5"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))' }}
        >
          {visiblePlans.map((p, ix) => {
            const popular = p.isMostPopular;
            const isCurrent = currentSub
              ? p.name === currentSub.planName && p.billingCycle === currentSub.billingCycle
              : Number(p.price) === 0;
            const { amount, suffix } = formatPlanPrice(p.price, p.billingCycle);
            const isFree = Number(p.price) === 0;
            const trialDays = trialDaysFor(p);

            return (
              <div
                key={p.id}
                className="card relative p-5 sm:p-6 flex flex-col animate-fadeIn sm:min-h-[440px]"
                style={{
                  animationDelay: ix * 80 + 'ms',
                  borderRadius: 16,
                  borderColor: popular ? 'var(--brand)' : 'var(--g100)',
                  borderWidth: popular ? 2 : 1,
                  boxShadow: popular
                    ? '0 4px 16px rgba(225,1,116,0.12)'
                    : undefined,
                }}
              >
                {popular && (
                  <div
                    className="absolute left-1/2 -translate-x-1/2 -top-3 flex items-center gap-1 px-3.5 py-1 rounded-full text-[11px] font-bold text-white whitespace-nowrap"
                    style={{ background: 'var(--brand)' }}
                  >
                    <Icon name="star" size={12} color="#fff" />
                    Most Popular
                  </div>
                )}
                {isCurrent && (
                  <div
                    className="absolute right-4 top-4 font-bold uppercase"
                    style={{
                      fontSize: 10,
                      padding: '3px 8px',
                      borderRadius: 100,
                      background: 'var(--green-bg)',
                      color: 'var(--green)',
                      letterSpacing: '0.6px',
                    }}
                  >
                    Current
                  </div>
                )}

                <div
                  className="font-extrabold text-ink mb-1"
                  style={{ fontSize: 20, letterSpacing: '-0.3px' }}
                >
                  {p.name}
                </div>
                {p.description && (
                  <div className="text-g500 mb-4" style={{ fontSize: 13 }}>
                    {p.description}
                  </div>
                )}

                <div className="flex items-baseline gap-2 mb-2">
                  <span
                    className="font-extrabold"
                    style={{
                      fontSize: 36,
                      color: popular ? 'var(--brand)' : 'var(--ink)',
                    }}
                  >
                    {amount}
                  </span>
                  {suffix && (
                    <span className="text-g500 font-medium" style={{ fontSize: 14 }}>
                      {suffix}
                    </span>
                  )}
                </div>

                {trialDays > 0 && (
                  <div
                    className="mb-4 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full self-start"
                    style={{
                      background: 'var(--green-bg)',
                      color: 'var(--green)',
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  >
                    <Icon name="calendar" size={12} color="var(--green)" />
                    {trialDays}-day free trial
                  </div>
                )}

                <ul className="list-none flex flex-col gap-2.5 flex-1 p-0 m-0 mb-5">
                  {p.features.length === 0 ? (
                    <li className="text-g400 italic" style={{ fontSize: 13 }}>
                      No features listed
                    </li>
                  ) : (
                    p.features.map((f, fi) => (
                      <li
                        key={fi}
                        className="flex items-start gap-2 text-g700"
                        style={{ fontSize: 13.5 }}
                      >
                        <span
                          className="rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                          style={{
                            width: 18,
                            height: 18,
                            background: popular
                              ? 'var(--brand-bg)'
                              : 'var(--green-bg)',
                          }}
                        >
                          <Icon
                            name="check-circle"
                            size={12}
                            color={popular ? 'var(--brand)' : 'var(--green)'}
                          />
                        </span>
                        {f}
                      </li>
                    ))
                  )}
                </ul>

                {/* On a paid plan, "Switch to Free" makes no sense — the agent
                    downgrades by cancelling (or letting the plan expire). */}
                {isFree && !isCurrent && onPaidPlan ? (
                  <div
                    className="text-center text-g400"
                    style={{ fontSize: 12.5, padding: '11px 0' }}
                  >
                    {canCancel
                      ? 'Cancel your subscription to return to Free.'
                      : 'Your plan reverts to Free when your current subscription expires.'}
                  </div>
                ) : (
                  <button
                    className={
                      isCurrent && !isCancelledRunning ? 'btn btn-secondary' : 'btn btn-primary'
                    }
                    style={{
                      width: '100%',
                      justifyContent: 'center',
                      paddingTop: 11,
                      paddingBottom: 11,
                    }}
                    onClick={() => {
                      // A cancelled plan that still runs is resumed, not re-bought.
                      if (isCurrent && isCancelledRunning) {
                        handleResume();
                        return;
                      }
                      if (isCurrent) return;
                      // A subscription is still running or being billed —
                      // subscribing on top of it would double-charge.
                      if (currentSub) {
                        toast(
                          isCancelledRunning
                            ? 'Resume your current plan, or subscribe again once it ends.'
                            : 'Cancel your current subscription before switching plans.',
                          'info'
                        );
                        return;
                      }
                      if (isFree) {
                        toast(
                          'Free plan switching is not yet wired up. Please pick a paid plan.',
                          'info'
                        );
                        return;
                      }
                      handleSwitchClick(p);
                    }}
                    disabled={(isCurrent && !isCancelledRunning) || resuming}
                  >
                    {isCurrent && isCancelledRunning
                      ? resuming
                        ? 'Resuming…'
                        : 'Resume subscription'
                      : isCurrent
                      ? 'Current plan'
                      : trialDays > 0
                      ? `Start ${trialDays}-day free trial`
                      : mySub
                      ? `Subscribe to ${p.name}`
                      : `Switch to ${p.name}`}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Payment modal — only mounted when a paid plan is being purchased. */}
      <Modal
        open={payingFor !== null}
        title={payingFor ? `Subscribe to ${payingFor.name}` : 'Subscribe'}
        onClose={() => setPayingFor(null)}
        width={640}
      >
        {payingFor && stripePromise ? (
          <Elements stripe={stripePromise}>
            <PaymentForm
              plan={payingFor}
              trialDays={trialDaysFor(payingFor)}
              onSuccess={handlePaymentSuccess}
              onCancel={() => setPayingFor(null)}
            />
          </Elements>
        ) : (
          <div className="text-sm text-g500">{STRIPE_UNAVAILABLE}</div>
        )}
      </Modal>

      {/* Cancel subscription confirmation */}
      <ConfirmDialog
        open={confirmingCancel}
        title={mySub?.status === 'trialing' ? 'Cancel free trial' : 'Cancel subscription'}
        variant="danger"
        confirmLabel="Yes, cancel"
        cancelLabel="Keep my plan"
        busy={cancelling}
        message={
          mySub && mySub.status === 'trialing' ? (
            <>
              Cancel your{' '}
              <strong style={{ color: 'var(--ink)' }}>{mySub.planName}</strong> free
              trial? You won't be charged.
              <br />
              You'll keep access until{' '}
              <strong style={{ color: 'var(--ink)' }}>
                {fmtDate(mySub.trialEndsAt ?? mySub.expiryDate)}
              </strong>
              , when the trial ends.
            </>
          ) : mySub ? (
            <>
              Are you sure you want to cancel your{' '}
              <strong style={{ color: 'var(--ink)' }}>{mySub.planName}</strong>{' '}
              subscription?
              <br />
              You'll keep access until{' '}
              <strong style={{ color: 'var(--ink)' }}>
                {new Date(mySub.expiryDate).toLocaleDateString()}
              </strong>
              , after which it won't renew.
            </>
          ) : (
            'Are you sure you want to cancel?'
          )
        }
        onConfirm={handleConfirmCancel}
        onCancel={() => {
          if (!cancelling) setConfirmingCancel(false);
        }}
      />
    </div>
  );
};

export default AgentSubscription;
